import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DocumentDuplicateIcon,
  EyeIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../components/ui/Modal';
import {
  badgeClass,
  ConfirmDialog,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/ui/FormControls';
import {
  useCloneRole,
  useCreateRole,
  useDeleteRole,
  usePermissionCatalogue,
  useRoleDetail,
  useRoleList,
  useRoleSummary,
  useUpdateRole,
} from '../hooks/useRoleManagement';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionAction, PermissionArea, PermissionCatalogue, Role } from '../services/role.service';
import { cn } from '../lib/utils';

const ACTION_ORDER: PermissionAction[] = ['read', 'create', 'update', 'delete', 'approve', 'export', 'execute'];

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const code = (area: PermissionArea, action: PermissionAction) => `${area.key}.${action}`;

// "Sales: Add" for "sales.create"
const describe = (catalogue: PermissionCatalogue | undefined, permission: string) => {
  const [areaKey, action] = permission.split('.');
  const area = catalogue?.areas.find((entry) => entry.key === areaKey);
  return area ? `${area.label}: ${catalogue?.actions[action as PermissionAction] ?? action}` : permission;
};

const groupsOf = (catalogue: PermissionCatalogue) => {
  const groups: { name: string; areas: PermissionArea[] }[] = [];
  catalogue.areas.forEach((area) => {
    const group = groups.find((entry) => entry.name === area.group);
    if (group) group.areas.push(area);
    else groups.push({ name: area.group, areas: [area] });
  });
  return groups;
};

interface RoleFormProps {
  role: Role | null;
  catalogue: PermissionCatalogue;
  // What the signed-in user holds: nobody hands out more than that
  ownPermissions: string[];
  isAdministrator: boolean;
  // Editing the role the signed-in user holds themselves
  isOwnRole: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (data: { displayName: string; description: string; priority: number; isActive: boolean; permissions: string[] }) => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ role, catalogue, ownPermissions, isAdministrator, isOwnRole, isSaving, onCancel, onSubmit }) => {
  const isEdit = !!role;
  // The Administrator role always holds everything
  const fixed = !!role?.isAdministrator;
  // Only an administrator changes the permissions of the role they hold themselves
  const permissionsLocked = fixed || (isOwnRole && !isAdministrator);

  const [displayName, setDisplayName] = useState(role?.displayName ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [priority, setPriority] = useState(String(role?.priority ?? 30));
  const [isActive, setIsActive] = useState(role?.isActive ?? true);
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissions ?? []));
  // Permissions switched on because another one needs them, to tell the user about
  const [added, setAdded] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const groups = useMemo(() => groupsOf(catalogue), [catalogue]);
  const mayGrant = (permission: string) => isAdministrator || ownPermissions.includes(permission);

  const toggle = (area: PermissionArea, action: PermissionAction) => {
    const permission = code(area, action);
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(permission)) {
        next.delete(permission);
        // Nothing in an area works without viewing it
        if (action === 'read') area.actions.forEach((other) => next.delete(code(area, other)));
        setAdded([]);
      } else {
        const extra: string[] = [];
        const add = (wanted: string) => {
          if (!next.has(wanted) && mayGrant(wanted)) {
            next.add(wanted);
            extra.push(wanted);
          }
        };
        next.add(permission);
        // Adding, editing and the rest need viewing; the pages of some areas need to read others
        if (action !== 'read' && area.actions.includes('read')) add(code(area, 'read'));
        (area.needs ?? []).forEach(add);
        setAdded(extra);
      }
      return next;
    });
  };

  const setArea = (area: PermissionArea, on: boolean) => {
    setSelected((previous) => {
      const next = new Set(previous);
      area.actions.forEach((action) => {
        const permission = code(area, action);
        if (!on) next.delete(permission);
        else if (mayGrant(permission)) next.add(permission);
      });
      if (on) (area.needs ?? []).forEach((needed) => mayGrant(needed) && next.add(needed));
      return next;
    });
    setAdded([]);
  };

  const applyPreset = (preset: 'none' | 'view' | 'all') => {
    const next = new Set<string>();
    if (preset !== 'none') {
      catalogue.areas.forEach((area) =>
        area.actions.forEach((action) => {
          const permission = code(area, action);
          if ((preset === 'all' || action === 'read') && mayGrant(permission)) next.add(permission);
        }),
      );
    }
    setSelected(next);
    setAdded([]);
  };

  // Areas whose pages will miss a dropdown because a permission they lean on is off
  const missing = useMemo(
    () =>
      catalogue.areas
        .filter((area) => area.actions.some((action) => selected.has(code(area, action))))
        .flatMap((area) => (area.needs ?? []).filter((needed) => !selected.has(needed)).map((needed) => ({ area, needed }))),
    [catalogue, selected],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (displayName.trim().length < 2) found.displayName = 'Give the role a name';
    const rank = Number(priority);
    if (!role?.isSystem && (!Number.isInteger(rank) || rank < 0 || rank > 99)) found.priority = 'Use a whole number from 0 to 99';
    setErrors(found);
    if (Object.keys(found).length > 0) {
      document.getElementById(found.displayName ? 'role-displayName' : 'role-priority')?.focus();
      return;
    }
    onSubmit({
      displayName: displayName.trim(),
      description: description.trim(),
      priority: rank,
      isActive,
      permissions: catalogue.areas.flatMap((area) => area.actions.map((action) => code(area, action))).filter((permission) => selected.has(permission)),
    });
  };

  const selectedCount = fixed ? catalogue.total : selected.size;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
        <Field label="Role Name" htmlFor="role-displayName" required error={errors.displayName} className="sm:col-span-3" hint={isEdit ? `Key: ${role?.name}` : 'For example "Store Keeper"'}>
          <input
            id="role-displayName"
            type="text"
            maxLength={60}
            autoFocus={!isEdit}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className={cn(inputClass, errors.displayName && inputErrorClass)}
          />
        </Field>
        <Field label="Order" htmlFor="role-priority" error={errors.priority} className="sm:col-span-1" hint="Higher is listed first">
          <input
            id="role-priority"
            type="number"
            min="0"
            max="99"
            value={priority}
            disabled={!!role?.isSystem}
            onChange={(event) => setPriority(event.target.value)}
            className={cn(inputClass, errors.priority && inputErrorClass)}
          />
        </Field>
        <div className="sm:col-span-2">
          <p className="block text-sm font-medium text-gray-700">Status</p>
          <label className="mt-2 flex items-start gap-2">
            <input
              type="checkbox"
              checked={isActive}
              disabled={fixed || (!!role && role.userCount > 0 && role.isActive)}
              onChange={(event) => setIsActive(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              Active
              <span className="block text-xs text-gray-500">
                {fixed
                  ? 'Always on'
                  : role && role.userCount > 0 && role.isActive
                    ? `${role.userCount} ${role.userCount === 1 ? 'user holds' : 'users hold'} it, so it stays on`
                    : 'An inactive role cannot be given to anyone'}
              </span>
            </span>
          </label>
        </div>
        <Field label="Description" htmlFor="role-description" className="sm:col-span-6">
          <input
            id="role-description"
            type="text"
            maxLength={300}
            value={description ?? ''}
            onChange={(event) => setDescription(event.target.value)}
            className={inputClass}
            placeholder="Who this role is for"
          />
        </Field>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">What this role may do</h3>
            <p className="text-xs text-gray-500">
              {selectedCount} of {catalogue.total} permissions. Everyone can always use their own profile, settings and notifications.
            </p>
          </div>
          {!permissionsLocked && (
            <div className="inline-flex rounded-lg bg-gray-100 p-1 text-sm">
              <button type="button" onClick={() => applyPreset('view')} className="rounded-md px-3 py-1 font-medium text-gray-600 hover:text-gray-900">
                View only
              </button>
              <button type="button" onClick={() => applyPreset('all')} className="rounded-md px-3 py-1 font-medium text-gray-600 hover:text-gray-900">
                Everything
              </button>
              <button type="button" onClick={() => applyPreset('none')} className="rounded-md px-3 py-1 font-medium text-gray-600 hover:text-gray-900">
                Clear
              </button>
            </div>
          )}
        </div>

        {permissionsLocked && (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700 ring-1 ring-inset ring-gray-200">
            <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            {fixed
              ? 'The Administrator role always has every permission, so that someone can always manage the system.'
              : 'This is the role you hold yourself. Its permissions can only be changed by an administrator.'}
          </p>
        )}
        {added.length > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-indigo-50 p-3 text-sm text-indigo-900 ring-1 ring-inset ring-indigo-600/20">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
            <span>Also switched on, because it is needed: {added.map((permission) => describe(catalogue, permission)).join(', ')}.</span>
          </p>
        )}
        {missing.length > 0 && !permissionsLocked && (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              Some forms will have empty dropdowns:{' '}
              {missing.map(({ area, needed }) => `${area.label} needs ${describe(catalogue, needed)}`).join('; ')}.
            </span>
          </p>
        )}

        <div className="mt-3 max-h-[26rem] overflow-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_#e5e7eb]">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Area
                </th>
                {ACTION_ORDER.map((action) => (
                  <th key={action} scope="col" className="w-16 px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {catalogue.actions[action]}
                  </th>
                ))}
                <th scope="col" className="w-14 px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  All
                </th>
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.name} className="divide-y divide-gray-100">
                <tr className="bg-gray-50/70">
                  <th colSpan={ACTION_ORDER.length + 2} scope="colgroup" className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500">
                    {group.name}
                  </th>
                </tr>
                {group.areas.map((area) => {
                  const grantable = area.actions.filter((action) => mayGrant(code(area, action)));
                  const allOn = fixed || (grantable.length > 0 && grantable.every((action) => selected.has(code(area, action))));
                  return (
                    <tr key={area.key} className="hover:bg-gray-50">
                      <th scope="row" className="px-3 py-2 text-left font-normal">
                        <p className="font-medium text-gray-900">{area.label}</p>
                        <p className="text-xs text-gray-500">{area.description}</p>
                      </th>
                      {ACTION_ORDER.map((action) => {
                        if (!area.actions.includes(action)) {
                          return <td key={action} className="px-2 py-2 text-center text-gray-200">·</td>;
                        }
                        const permission = code(area, action);
                        const allowed = mayGrant(permission);
                        const label = `${area.label}: ${catalogue.actions[action]}`;
                        return (
                          <td key={action} className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              data-permission={permission}
                              checked={fixed || selected.has(permission)}
                              disabled={permissionsLocked || (!allowed && !selected.has(permission))}
                              onChange={() => toggle(area, action)}
                              aria-label={label}
                              title={
                                !allowed && !permissionsLocked
                                  ? `${label}. You do not have this permission yourself, so you cannot give it.`
                                  : area.actionHints?.[action] ?? label
                              }
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                            />
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={allOn}
                          disabled={permissionsLocked || grantable.length === 0}
                          onChange={(event) => setArea(area, event.target.checked)}
                          aria-label={`Everything in ${area.label}`}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      </div>

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isSaving} className={primaryButtonClass}>
          {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Role'}
        </button>
      </FormActions>
    </form>
  );
};

const RoleManagement: React.FC = () => {
  const { can, permissions: ownPermissions, isAdministrator, role: ownRole } = usePermissions();
  const canAdd = can('roles.create');
  const canEdit = can('roles.update');
  const canDelete = can('roles.delete');

  const { data: roles = [], isLoading, isError, error, refetch } = useRoleList();
  const { data: summary } = useRoleSummary();
  const { data: catalogue } = usePermissionCatalogue();

  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState<Role | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [deleting, setDeleting] = useState<Role | null>(null);

  const { data: viewing, isLoading: viewingLoading } = useRoleDetail(viewingId);
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const cloneRole = useCloneRole();
  const deleteRole = useDeleteRole();

  // There are only ever a handful of roles, so the list is filtered here instead of on the server
  const visible = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    return roles.filter((role) => {
      if (statusFilter === 'active' && !role.isActive) return false;
      if (statusFilter === 'inactive' && role.isActive) return false;
      if (statusFilter === 'custom' && role.isSystem) return false;
      if (statusFilter === 'builtin' && !role.isSystem) return false;
      return !term || role.displayName.toLowerCase().includes(term) || role.name.includes(term) || (role.description ?? '').toLowerCase().includes(term);
    });
  }, [roles, searchInput, statusFilter]);

  useEffect(() => {
    if (cloning) setCloneName(`${cloning.displayName} (copy)`);
  }, [cloning]);

  const isSaving = createRole.isPending || updateRole.isPending;
  const closeForm = () => {
    if (!isSaving) setFormOpen(false);
  };
  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (role: Role) => {
    setViewingId(null);
    setEditing(role);
    setFormOpen(true);
  };

  const fail = (fallback: string) => (actionError: unknown) => toast.error(getApiErrorMessage(actionError, fallback), { id: 'forbidden' });

  const handleSubmit = (data: { displayName: string; description: string; priority: number; isActive: boolean; permissions: string[] }) => {
    if (editing) {
      // The Administrator's permissions and the order of built-in roles are fixed, so they are not sent
      const changes = {
        displayName: data.displayName,
        description: data.description,
        ...(editing.isAdministrator ? {} : { permissions: data.permissions, isActive: data.isActive }),
        ...(editing.isSystem ? {} : { priority: data.priority }),
      };
      updateRole.mutate(
        { id: editing.id, data: changes },
        {
          onSuccess: (saved) => {
            toast.success(
              saved.userCount > 0
                ? `${saved.displayName} updated. It applies to its ${saved.userCount} ${saved.userCount === 1 ? 'user' : 'users'} straight away.`
                : `${saved.displayName} updated`,
            );
            setFormOpen(false);
          },
          onError: fail('The role could not be saved'),
        },
      );
    } else {
      createRole.mutate(data, {
        onSuccess: (saved) => {
          toast.success(`${saved.displayName} added. Give it to people on the Users page.`);
          setFormOpen(false);
        },
        onError: fail('The role could not be added'),
      });
    }
  };

  const handleClone = (event: React.FormEvent) => {
    event.preventDefault();
    if (!cloning || cloneName.trim().length < 2) return;
    cloneRole.mutate(
      { id: cloning.id, displayName: cloneName.trim() },
      {
        onSuccess: (saved) => {
          toast.success(`${saved.displayName} added as a copy`);
          setCloning(null);
        },
        onError: fail('The role could not be copied'),
      },
    );
  };

  const handleToggleActive = (role: Role) => {
    updateRole.mutate(
      { id: role.id, data: { isActive: !role.isActive } },
      {
        onSuccess: () => toast.success(`${role.displayName} switched ${role.isActive ? 'off' : 'on'}`),
        onError: fail('The status could not be changed'),
      },
    );
  };

  const handleDelete = () => {
    if (!deleting) return;
    deleteRole.mutate(deleting.id, {
      onSuccess: () => {
        toast.success(`${deleting.displayName} deleted`);
        setDeleting(null);
      },
      onError: (actionError) => {
        fail('The role could not be deleted')(actionError);
        setDeleting(null);
      },
    });
  };

  const total = catalogue?.total ?? summary?.permissionCount ?? 0;
  const stats = [
    { label: 'Roles', value: summary?.total, hint: summary ? `${summary.active} active` : '' },
    { label: 'Your own roles', value: summary?.custom, hint: 'Added here, besides the built-in ones' },
    { label: 'Permissions', value: summary?.permissionCount, hint: 'Things a role can be allowed' },
    {
      label: 'Users without access',
      value: summary?.usersWithoutAccess,
      hint: summary?.usersWithoutAccess ? 'Their role is switched off' : 'Everyone can sign in',
      warn: !!summary?.usersWithoutAccess,
    },
  ];

  const iconButton = 'rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Roles</h1>
          <p className="mt-1 text-sm text-gray-500">
            What each role may see and do. A change applies to everyone holding the role on their next click.
          </p>
        </div>
        {canAdd && (
          <button type="button" onClick={openAdd} disabled={!catalogue} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Role
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.warn ? 'text-amber-600' : 'text-gray-900')}>
              {stat.value === undefined ? '—' : formatNumber(stat.value)}
            </p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search roles"
              aria-label="Search roles"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Show" className={cn(inputClass, 'sm:w-48')}>
            <option value="">All roles</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="builtin">Built-in</option>
            <option value="custom">Your own</option>
          </select>
          {(searchInput || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setStatusFilter('');
              }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The roles could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Role</th>
                  <th scope="col" className={thClass}>Permissions</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Users</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 5 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : visible.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">No role matches</p>
                      <p className="mt-1 text-sm text-gray-500">Try a different search or filter.</p>
                    </td>
                  </tr>
                ) : (
                  visible.map((role) => {
                    const share = total > 0 ? Math.round((role.permissions.length / total) * 100) : 0;
                    const isOwn = role.name === ownRole;
                    return (
                      <tr key={role.id} className={cn('hover:bg-gray-50', !role.isActive && 'bg-gray-50/60')}>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{role.displayName}</span>
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">{role.name}</span>
                            {role.isSystem && <span className={cn(badgeClass, 'bg-gray-50 text-gray-600 ring-gray-500/20')}>Built-in</span>}
                            {isOwn && <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>Your role</span>}
                          </div>
                          {role.description && <p className="mt-0.5 max-w-xl text-sm text-gray-500">{role.description}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
                              <div className={cn('h-full rounded-full', role.isAdministrator ? 'bg-green-500' : 'bg-indigo-600')} style={{ width: `${share}%` }} />
                            </div>
                            <span className="whitespace-nowrap text-sm text-gray-700 tabular-nums">
                              {role.isAdministrator ? 'Everything' : `${role.permissions.length} of ${total}`}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                          {role.userCount > 0 ? formatNumber(role.userCount) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={cn(
                              badgeClass,
                              role.isActive ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20',
                            )}
                          >
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => setViewingId(role.id)} title="View" aria-label={`View ${role.displayName}`} className={iconButton}>
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {canEdit && (
                              <button type="button" onClick={() => openEdit(role)} disabled={!catalogue} title="Edit" aria-label={`Edit ${role.displayName}`} className={iconButton}>
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canAdd && (
                              <button type="button" onClick={() => setCloning(role)} title="Copy" aria-label={`Copy ${role.displayName}`} className={iconButton}>
                                <DocumentDuplicateIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit && !role.isAdministrator && (
                              <button
                                type="button"
                                onClick={() => handleToggleActive(role)}
                                disabled={updateRole.isPending || (role.isActive && role.userCount > 0)}
                                title={role.isActive && role.userCount > 0 ? 'Move its users to another role first' : undefined}
                                className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {role.isActive ? 'Switch off' : 'Switch on'}
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => setDeleting(role)}
                                disabled={role.isSystem || role.userCount > 0}
                                title={role.isSystem ? 'Built-in roles cannot be deleted' : role.userCount > 0 ? 'Move its users to another role first' : 'Delete'}
                                aria-label={`Delete ${role.displayName}`}
                                className={cn(iconButton, !role.isSystem && role.userCount === 0 && 'hover:bg-red-50 hover:text-red-600')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
          Showing {visible.length} of {roles.length} roles
        </div>
      </div>

      {/* Add / edit */}
      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${editing.displayName}` : 'Add Role'}
        description={editing ? 'Changes apply to everyone holding the role on their next click.' : 'Name the role and tick what it may do.'}
        size="2xl"
      >
        {formOpen && catalogue && (
          <RoleForm
            key={editing?.id ?? 'new'}
            role={editing}
            catalogue={catalogue}
            ownPermissions={ownPermissions}
            isAdministrator={isAdministrator}
            isOwnRole={!!editing && editing.name === ownRole}
            isSaving={isSaving}
            onCancel={closeForm}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>

      {/* View */}
      <Modal isOpen={!!viewingId} onClose={() => setViewingId(null)} title="Role details" size="xl">
        {viewingLoading || !viewing || !catalogue ? (
          <div className="space-y-3">
            <div className="h-6 w-1/3 animate-pulse rounded bg-gray-100" />
            <div className="h-24 animate-pulse rounded bg-gray-100" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-indigo-50 p-2">
                <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold text-gray-900">{viewing.displayName}</p>
                  {viewing.isSystem && <span className={cn(badgeClass, 'bg-gray-50 text-gray-600 ring-gray-500/20')}>Built-in</span>}
                  <span
                    className={cn(badgeClass, viewing.isActive ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}
                  >
                    {viewing.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{viewing.description || 'No description'}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {viewing.isAdministrator ? 'May do everything' : `May do ${viewing.permissions.length} of ${catalogue.total} things`}
              </p>
              <dl className="divide-y divide-gray-100">
                {catalogue.areas.map((area) => {
                  const held = area.actions.filter((action) => viewing.permissions.includes(code(area, action)));
                  return (
                    <div key={area.key} className="flex flex-col gap-1 px-4 py-2 sm:flex-row sm:items-center sm:gap-4">
                      <dt className={cn('w-44 shrink-0 text-sm', held.length > 0 ? 'font-medium text-gray-900' : 'text-gray-400')}>{area.label}</dt>
                      <dd className="flex flex-wrap gap-1">
                        {held.length === 0 ? (
                          <span className="text-sm text-gray-400">No access</span>
                        ) : (
                          held.map((action) => (
                            <span key={action} className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>
                              {catalogue.actions[action]}
                            </span>
                          ))
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>

            <div className="rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {viewing.userCount === 0 ? 'Nobody holds this role' : `Held by ${viewing.userCount} ${viewing.userCount === 1 ? 'user' : 'users'}`}
              </p>
              {viewing.users.length > 0 && (
                <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto">
                  {viewing.users.map((user) => (
                    <li key={user.id} className="flex items-center justify-between gap-3 px-4 py-2">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900">{user.name}</span>
                        <span className="block truncate text-xs text-gray-500">{user.email}</span>
                      </span>
                      {user.status !== 'active' && <span className={cn(badgeClass, 'bg-amber-50 text-amber-700 ring-amber-600/20')}>{user.status}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <FormActions>
              <button type="button" onClick={() => setViewingId(null)} className={secondaryButtonClass}>
                Close
              </button>
              {canEdit && (
                <button type="button" onClick={() => openEdit(viewing)} className={primaryButtonClass}>
                  Edit Role
                </button>
              )}
            </FormActions>
          </div>
        )}
      </Modal>

      {/* Copy */}
      <Modal
        isOpen={!!cloning}
        onClose={() => !cloneRole.isPending && setCloning(null)}
        title="Copy role"
        description={cloning ? `A new role with the same permissions as ${cloning.displayName}. Nobody holds it until you give it to them.` : undefined}
        size="md"
      >
        {cloning && (
          <form onSubmit={handleClone} className="space-y-4">
            <Field label="Name of the Copy" htmlFor="clone-name" required>
              <input id="clone-name" type="text" maxLength={60} autoFocus value={cloneName} onChange={(event) => setCloneName(event.target.value)} className={inputClass} />
            </Field>
            <FormActions>
              <button type="button" onClick={() => setCloning(null)} disabled={cloneRole.isPending} className={secondaryButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={cloneRole.isPending || cloneName.trim().length < 2} className={primaryButtonClass}>
                {cloneRole.isPending ? 'Copying…' : 'Copy Role'}
              </button>
            </FormActions>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete role"
        message={
          deleting ? (
            <>
              Delete <span className="font-semibold">{deleting.displayName}</span>? Nobody holds it, so nobody loses access. This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={deleteRole.isPending}
      />
    </div>
  );
};

export default RoleManagement;
